import { styled as muiStyled } from "@mui/material/styles";

/**
 * Props MUI consumes internally and never forwards to the DOM. MUI's default `shouldForwardProp`
 * forwards every prop except these four, so a custom `shouldForwardProp` must re-exclude them.
 */
const MUI_INTERNAL_PROPS = new Set<string>(["ownerState", "theme", "sx", "as"]);

/** Whether a prop should reach the DOM: not a `$`-prefixed transient prop, not a MUI internal. */
function forwardToDom(prop: PropertyKey): boolean {
  return typeof prop === "string" && !prop.startsWith("$") && !MUI_INTERNAL_PROPS.has(prop);
}

/**
 * MUI `styled` with styled-components-style transient props: a `$`-prefixed prop drives styling but
 * is stripped before the DOM. Import this instead of `@mui/material/styles`'s `styled` when wrapping
 * a MUI component (`styled(Stack)`, `styled(Chip)`, `styled(ListItem)`, …) with `$foo` styling props.
 * MUI — unlike emotion's string-tag path, which already filters unknown attrs — otherwise forwards
 * them to the DOM and React warns about unknown attributes.
 */
export const styled = ((
  tag: Parameters<typeof muiStyled>[0],
  options?: Parameters<typeof muiStyled>[1]
) => muiStyled(tag, { shouldForwardProp: forwardToDom, ...options })) as typeof muiStyled;
